import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Card, PageHeader, Button, Input, Select, Table, Th, Td } from "../components/ui";
import { errMsg, fmtIDR } from "../lib/utils";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import NumberInput from "../components/NumberInput";
import Pagination, { usePagination } from "../components/Pagination";

const empty = { customer_id: "", origin: "", destination: "", price_per_kg: 0, via: "", minimum_kg: "" };

const VIA_LABEL = { D: "Darat", L: "Laut", U: "Udara" };

export default function Contracts() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [c, cu] = await Promise.all([api.get("/contracts"), api.get("/customers")]);
    setRows(c.data); setCustomers(cu.data);
  };
  useEffect(() => { load(); }, []);

  const cName = (id) => customers.find((c) => c.id === id)?.name || "-";

  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [cName(r.customer_id), r.origin, r.destination, r.via]
      .some((v) => (v || "").toString().toLowerCase().includes(s)));
  }, [rows, q, customers]);
  const pag = usePagination(filtered, { resetKey: q });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contracts", {
        ...form,
        price_per_kg: parseFloat(form.price_per_kg),
        minimum_kg: form.minimum_kg === "" ? null : parseFloat(form.minimum_kg),
        via: form.via || null,
      });
      toast.success("Perjanjian Rate dibuat");
      setOpen(false); setForm(empty); load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus perjanjian rate?")) return;
    await api.delete(`/contracts/${id}`); load();
  };

  return (
    <div data-testid="contracts-page">
      <PageHeader
        title="Perjanjian Rate"
        subtitle="Daftar tarif customer per rute (origin–destination) & moda (Darat/Laut/Udara)."
        action={<Button data-testid="add-contract-btn" onClick={() => setOpen(true)}><Plus size={16}/> Tambah Perjanjian</Button>}
      />
      <Card>
        <div className="p-3 border-b border-slate-200 flex items-center gap-2">
          <Input data-testid="contracts-search" placeholder="Cari customer / origin / destination..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md"/>
          <span className="text-xs text-slate-500 ml-auto">{filtered.length} dari {rows.length} perjanjian</span>
        </div>
        <Table>
          <thead><tr>
            <Th>Customer</Th><Th>Origin</Th><Th>Destination</Th><Th>Via</Th>
            <Th className="text-right">Tarif / Kg</Th><Th className="text-right">Min Kg</Th><Th></Th>
          </tr></thead>
          <tbody>
            {pag.paged.map((r) => (
              <tr key={r.id} data-testid={`contract-row-${r.id}`}>
                <Td><span className="font-semibold">{cName(r.customer_id)}</span></Td>
                <Td className="font-mono">{r.origin}</Td>
                <Td className="font-mono">{r.destination}</Td>
                <Td>
                  {r.via ? (
                    <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase">
                      {r.via} — {VIA_LABEL[r.via] || ""}
                    </span>
                  ) : <span className="text-slate-400 text-xs">—</span>}
                </Td>
                <Td className="text-right font-semibold">{fmtIDR(r.price_per_kg)}</Td>
                <Td className="text-right text-xs">{r.minimum_kg ? `${r.minimum_kg} kg` : <span className="text-slate-400">—</span>}</Td>
                <Td className="text-right"><button onClick={() => del(r.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={14}/></button></Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><Td colSpan={7} className="text-center text-slate-500 py-8">Belum ada perjanjian rate.</Td></tr>}
          </tbody>
        </Table>
        <Pagination {...pag} testid="pagination-contracts"/>
      </Card>

      {open && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-heading font-bold text-xl">Tambah Perjanjian Rate</h3>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1.5">Customer *</label>
                <Select data-testid="contract-customer-select" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} required>
                  <option value="">— Pilih Customer —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[11px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1.5">Origin *</label>
                  <Input data-testid="contract-origin-input" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value.toUpperCase() })} required placeholder="CGK" /></div>
                <div><label className="block text-[11px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1.5">Destination *</label>
                  <Input data-testid="contract-destination-input" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value.toUpperCase() })} required placeholder="DPS" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1.5">Via</label>
                  <Select data-testid="contract-via-select" value={form.via} onChange={(e) => setForm({ ...form, via: e.target.value })}>
                    <option value="">— Pilih —</option>
                    <option value="D">Darat</option>
                    <option value="L">Laut</option>
                    <option value="U">Udara</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1.5">Harga per Kg (IDR) *</label>
                  <NumberInput data-testid="contract-price-input" value={form.price_per_kg} onChange={(e) => setForm({ ...form, price_per_kg: e.target.value })} required />
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1.5">Minimum Berat (Kg) — opsional</label>
                <NumberInput decimal data-testid="contract-min-kg-input" value={form.minimum_kg} onChange={(e) => setForm({ ...form, minimum_kg: e.target.value })} placeholder="Mis. 50 (artinya order < 50kg tetap ditagih 50kg)"/>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" type="button" onClick={() => setOpen(false)}>Batal</Button>
                <Button data-testid="save-contract-btn" type="submit">Simpan</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
